import { supabase } from "../../lib/supabase";

// AI Borg Studio data. Like the dashboard, everything goes through admin-only functions
// (is_borg_admin()), defined in supabase/migrations/20261013000000_borg_ai_studio.sql; the tables
// behind them have no grants.
//
//   get_borg_studio(p_ratings = 200) -> {
//     prompts:  [{ id, version, label, system_prompt, temperature, example_count, is_live,
//                  created_at, ratings: { good, bad, fixed, model_good, model_rated } }]
//                                                                      oldest version first
//     examples: [{ id, original, name, enabled, source, created_at }]    newest first
//     ratings:  [{ id, prompt_id, theme, original, name, source, verdict, reason, fixed_name,
//                  created_at }]                                         newest first, capped
//   }
//   get_borg_ai_feedback(p_days = 30) -> [{ name, original, source, rolls, last_rolled_at, likes,
//                                           dislikes }]   live AI names members reacted to
//     (supabase/migrations/20261015000000_borg_ai_dislikes.sql)
//   get_borg_ai_training_data() -> { ratings: [...every rating, oldest first], liked: [{ name,
//                                    original, source, likes, dislikes }], examples: [{ original,
//                                    name }] }   fine-tuning sources, built into files by
//     trainingData.js (supabase/migrations/20261016000000_borg_ai_training.sql)
// The board pool (names already on the board, for the example pool's quick add) is public.borgs,
// shared through BorgsContext (supabase/migrations/20261018000000_borg_catalog.sql).
//   save_borg_prompt(p_label, p_system_prompt, p_temperature, p_example_count) -> prompt
//     Versions are immutable so every rating stays tied to the exact prompt that made the name;
//     editing saves the next version.
//   set_live_borg_prompt(p_id) -> void         exactly one version is live afterwards
//   add_borg_example(p_original, p_name) -> example
//   set_borg_example_enabled(p_id, p_enabled) -> void
//   rate_borg_name(p_id, p_prompt_id, p_theme, p_original, p_name, p_source, p_verdict, p_reason,
//                  p_fixed_name, p_topic) -> rating   p_id null inserts, otherwise re-rates that row;
//                  p_topic (the batch's random topic) is kept only when the theme is blank
//     (supabase/migrations/20261019000000_borg_ai_rating_topics.sql)
//
// Test batches come from the borg-ai-studio edge function (admins only, spends no credits, logs
// no rolls; see supabase/functions/borg-ai-studio/index.ts):
//   { promptId, theme, topic?, avoid } -> { pairs: [{ original, name, source, swap }], dropped,
//                                           lost: [{ original, name, reason }], topic,
//                                           prompt: { system, user }, raw, ms }
//   lost: the model's originals that offer no name; reason "known-original" | "known-name" | "no-name".
//   topic (blank theme only) reuses a random topic, so two versions can be compared on one task.

/** Originals per batch, set by BATCH_SIZE in supabase/functions/_shared/borg-ai.ts. */
export const BATCH_SIZE = 5;
export const MAX_THEME_LENGTH = 120;

/** Letters and digits only, lower-cased: "Sponge Borg" and "SpongeBorg" share one key. */
export const nameKey = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "");

async function rpc(name, args) {
  const { data: result, error } = await supabase.rpc(name, args);
  if (error) throw new Error(error.message || "Request failed");
  return result;
}

// Member feedback is its own call so a failure there doesn't take the rest of the Studio down.
export async function fetchStudio() {
  const [result, feedback] = await Promise.all([
    rpc("get_borg_studio"),
    rpc("get_borg_ai_feedback").then(
      (rows) => ({ rows: rows ?? [], error: null }),
      (failure) => ({ rows: [], error: failure.message })
    ),
  ]);
  return {
    prompts: result?.prompts ?? [],
    examples: result?.examples ?? [],
    ratings: result?.ratings ?? [],
    feedback: feedback.rows,
    feedbackError: feedback.error,
  };
}

/** "borg" fused into a word, like the edge function and submissions require of every name. */
export const FUSED_BORG = /\w+borg|borg\w+/i;

/**
 * Every name on the board an example can be made from (BorgsContext rows), one per name. Names
 * without "borg" fused into a word can't be examples, so they're left out.
 */
export function boardPool(rows) {
  const pool = new Map();
  for (const row of rows) {
    const key = nameKey(row.name);
    if (key && FUSED_BORG.test(row.name) && !pool.has(key)) pool.set(key, { ...row, key });
  }
  return [...pool.values()];
}

export const savePrompt = ({ label, systemPrompt, temperature, exampleCount }) =>
  rpc("save_borg_prompt", {
    p_label: label,
    p_system_prompt: systemPrompt,
    p_temperature: temperature,
    p_example_count: exampleCount,
  });

export const setLivePrompt = (id) => rpc("set_live_borg_prompt", { p_id: id });

export async function fetchTrainingData() {
  const result = await rpc("get_borg_ai_training_data");
  return { ratings: result?.ratings ?? [], liked: result?.liked ?? [], examples: result?.examples ?? [] };
}

export const addExample = ({ original, name }) => rpc("add_borg_example", { p_original: original, p_name: name });

export const setExampleEnabled = (id, enabled) =>
  rpc("set_borg_example_enabled", { p_id: id, p_enabled: enabled });

export const rateName = ({ id, promptId, theme, topic, original, name, source, verdict, reason, fixedName }) =>
  rpc("rate_borg_name", {
    p_id: id ?? null,
    p_prompt_id: promptId,
    p_theme: theme,
    p_original: original,
    p_name: name,
    p_source: source ?? null,
    p_verdict: verdict,
    p_reason: reason || null,
    p_fixed_name: fixedName || null,
    p_topic: topic ?? null,
  });

/** `avoid`: the site's own names (BorgsContext), like a member roll. */
export async function generateBatch({ promptId, theme, topic, avoid }) {
  const { data: payload, error } = await supabase.functions.invoke("borg-ai-studio", {
    body: { promptId, theme, topic, avoid },
  });
  // Non-2xx responses still carry { error } in the body.
  const body = error ? await error.context?.json?.().catch(() => null) : payload;
  if (!Array.isArray(body?.pairs)) {
    // A timed-out batch still says what was sent, so the prompt can be trimmed.
    throw Object.assign(new Error(body?.error ?? "BORG AI couldn't be reached. Try again."), {
      prompt: body?.prompt ?? null,
      topic: body?.topic ?? null,
    });
  }
  return {
    pairs: body.pairs,
    dropped: body.dropped ?? [],
    lost: body.lost ?? [],
    topic: body.topic ?? null,
    prompt: body.prompt ?? null,
    raw: body.raw ?? "",
    ms: body.ms ?? null,
  };
}
